<script setup>
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import {storeToRefs} from 'pinia'
import {formatSum} from '../../../utils/helpers'
import {computed, defineProps, ref} from 'vue'

const {openInvoiceModal} = useDefaultStore()
const {user, isAgencyClient, userAvailableBalance, userRemainingBalanceDays, userBalanceStatus, canUseInvoices} =
  storeToRefs(useAuthenticationStore())

// Props & emits
const props = defineProps({
  isMini: {
    type: [Boolean, Number],
    default: false,
  },
})

// Refs
const showMiniPopup = ref(false)

// Computed
const buttonClass = computed(() => {
  if (props.isMini) {
    return [
      userRemainingBalanceDays.value <= 0 && userBalanceStatus.value === 'critical_balance'
        ? 'btn btn-primary'
        : 'btn-no-style text-primary fw-semibold fs-13-px',
    ]
  }

  return [
    userRemainingBalanceDays.value <= 0 && userBalanceStatus.value === 'critical_balance'
      ? 'btn btn-primary'
      : 'btn-no-style text-primary fw-bold',
  ]
})

const progressBarClass = computed(() => {
  if (props.isMini) {
    const mapper = {
      critical_balance: 'danger-circle',
      low_balance: 'warning-circle',
    }

    return ['balance-circle', mapper[userBalanceStatus.value]]
  }

  const mapper = {
    critical_balance: 'danger-bar',
    low_balance: 'warning-bar',
  }

  return ['balance-bar', mapper[userBalanceStatus.value]]
})

const showAlert = computed(
  () => isAgencyClient.value && userBalanceStatus.value !== 'ok' && user.value?.company?.payment_terms === 'prepay',
)
</script>

<template>
  <template v-if="showAlert">
    <div
      v-if="isMini"
      class="balance-alert__mini"
      @mouseenter="showMiniPopup = true"
      @mouseleave="showMiniPopup = false"
    >
      <div :class="progressBarClass" :style="`--percent: ${(userRemainingBalanceDays / 7) * 100}%`" />

      <div v-if="showMiniPopup" class="balance-alert__popup-wrapper">
        <div class="balance-alert__popup">
          <div class="balance-alert__popup__title">
            <div v-if="userRemainingBalanceDays > 0" class="fw-bold custom fs-5">
              <span v-if="userBalanceStatus === 'low_balance'">
                Low Funds — {{ formatSum(userAvailableBalance) }} Left
              </span>
              <span v-else-if="userBalanceStatus === 'critical_balance'">
                Almost Out — {{ formatSum(userAvailableBalance) }} Left
              </span>
            </div>
            <div v-else class="fw-bold custom fs-5">High Risk — {{ formatSum(userAvailableBalance) }} Left</div>
          </div>
          <div v-if="canUseInvoices" class="custom mt-3">
            <button @click="openInvoiceModal" :class="buttonClass">Generate Proforma Invoice</button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="custom px-8 mb-4">
      <div class="alert alert-light-grey balance-alert">
        <div v-if="userRemainingBalanceDays > 0" class="fw-semibold">
          <span v-if="userBalanceStatus === 'low_balance'">
            Low Funds — {{ formatSum(userAvailableBalance) }} Left
          </span>
          <span v-else-if="userBalanceStatus === 'critical_balance'">
            Almost Out — {{ formatSum(userAvailableBalance) }} Left
          </span>
        </div>
        <div v-else class="fw-semibold">High Risk — {{ formatSum(userAvailableBalance) }} Left</div>
        <div :class="progressBarClass">
          <div class="bar_inner" :style="`width: ${(userRemainingBalanceDays / 7) * 100}%`"></div>
        </div>
        <div v-if="canUseInvoices" class="custom mt-3">
          <button @click="openInvoiceModal" :class="buttonClass">Generate Proforma Invoice</button>
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="scss">
.warning-bar {
  background-color: rgba(255, 168, 0, 0.3);

  & .bar_inner {
    background-color: rgba(255, 168, 0, 1);
  }
}

.danger-bar {
  background-color: rgba(253, 45, 68, 0.3);

  & .bar_inner {
    background-color: rgba(253, 45, 68, 1);
  }
}

.balance-alert {
  padding-top: 12px;
  padding-bottom: 12px;
  border-radius: 8px;
}

.balance-bar {
  height: 8px;
  width: 100%;
  position: relative;
  border-radius: 10px;
  margin-top: 8px;

  & .bar_inner {
    border-radius: 10px;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
  }
}

.balance-alert__mini {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 8px;
}

.balance-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: conic-gradient(red 0%, lightgray 0%);
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    position: absolute;
    right: calc(50% - 12px);
    top: calc(50% - 12px);
    z-index: 1;
  }
}

.danger-circle {
  background: conic-gradient(rgba(253, 45, 68, 1) var(--percent), rgba(253, 45, 68, 0.2) 0);
}

.warning-circle {
  background: conic-gradient(#facc15 var(--percent), rgba(250, 204, 21, 0.2) 0);
}

.balance-alert__popup-wrapper {
  position: absolute;
  left: calc(100% - 16px);
  z-index: 100;
  padding-left: 20px;
}

.balance-alert__popup {
  width: max-content;
  background: #fff;
  padding: 16px 24px;
  position: relative;
  border-radius: 8px;
  box-shadow: 0 2px 10px 4px rgba(0, 0, 0, 0.15);

  &:after {
    content: '';
    display: block;
    width: 12px;
    height: 32px;
    background: url('~@shared-images/sidebar/ui/balance-alert-arrow.svg') center no-repeat;
    background-size: 100% 100%;
    position: absolute;
    right: 100%;
    top: calc(50% - 16px);
  }
}
</style>
